import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Alert from '../../../../components/Alert';
import Toast from '../../../../components/Toast';
import { useJobApplicationDetailsViewModel } from '../viewmodel/useJobApplicationDetailsViewModel';
import WithdrawConfirmationModal from '../components/WithdrawConfirmationModal';
import { formatDateTime } from '../../../../utils/datetime';
import { getJobTitleInitials } from '../../../../utils/jobTitleDefault';

const JobApplicationDetailsPage: React.FC = () => {
  const { id: applicationId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    state,
    handleWithdraw,
    handleDownloadResume,
    clearError,
    removeToast,
    clearToasts
  } = useJobApplicationDetailsViewModel(applicationId);

  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline'>('overview');

  // Clear toasts on unmount
  useEffect(() => {
    return () => {
      clearToasts();
    };
  }, []);

  const handleWithdrawClick = () => {
    clearError();
    setShowWithdrawModal(true);
  };

  const handleConfirmWithdraw = async () => {
    setShowWithdrawModal(false);
    await handleWithdraw(); // Toast is handled in viewmodel
  };

  const handleCloseModal = () => {
    setShowWithdrawModal(false);
    clearError();
  };

  const handleDownloadClick = () => {
    handleDownloadResume(); // Toast is handled in viewmodel
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'submitted': return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'in_review': return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'shortlisted': return 'bg-purple-100 text-purple-800 border border-purple-200';
      case 'interview_scheduled': return 'bg-indigo-100 text-indigo-800 border border-indigo-200';
      case 'interviewed': return 'bg-indigo-200 text-indigo-900 border border-indigo-300';
      case 'offered': return 'bg-green-100 text-green-800 border border-green-200';
      case 'hired': return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
      case 'rejected': return 'bg-red-100 text-red-800 border border-red-200';
      case 'withdrawn': return 'bg-gray-100 text-gray-800 border border-gray-200';
      case 'declined': return 'bg-orange-100 text-orange-800 border border-orange-200';
      case 'on_hold': return 'bg-gray-100 text-gray-800 border border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const getTimelineStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'submitted': return 'bg-blue-500';
      case 'in_review': return 'bg-yellow-500';
      case 'shortlisted': return 'bg-purple-500';
      case 'interview_scheduled': return 'bg-indigo-500';
      case 'interviewed': return 'bg-indigo-600';
      case 'offered': return 'bg-green-500';
      case 'hired': return 'bg-emerald-500';
      case 'rejected': return 'bg-red-500';
      case 'withdrawn': return 'bg-gray-500';
      default: return 'bg-gray-400';
    }
  };

  const canWithdrawApplication = (status: string) => {
    const normalizedStatus = status.toLowerCase();
    return !['withdrawn', 'rejected', 'declined', 'hired'].includes(normalizedStatus);
  };

  if (state.loading) {
    return (
      <div className="flex justify-center items-center min-h-screen p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-sm sm:text-base text-gray-600">Loading application details...</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
        <Alert type="error" message={state.error} onClose={clearError} />
      </div>
    );
  }

  if (!state.application) return null;

  const app = state.application;
  const jobInitials = getJobTitleInitials(app.job.title);

  // Tab navigation items
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'timeline', label: 'Timeline' },
  ];

  // Timeline component
  const Timeline = () => {
    if (!app.status_history || app.status_history.length === 0) {
      return (
        <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-200">
          <h2 className="text-base sm:text-lg font-semibold mb-4">Application Timeline</h2>
          <p className="text-gray-500 text-center py-4 text-sm">No status history available</p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold mb-6">Application Timeline</h2>

        <div className="relative pl-6">
          <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-gray-200"></div>

          <div className="space-y-6">
            {app.status_history.map((item, index) => (
              <div key={index} className="relative">
                <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-4 border-white shadow ${getTimelineStatusColor(item.status)}`} />

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex justify-between items-center">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {item.status.replace(/_/g, ' ').toUpperCase()}
                    </h3>
                    <span className="text-xs text-gray-500">
                      {formatDateTime(item.changed_at)}
                    </span>
                  </div>

                  {item.notes && (
                    <p className="mt-2 text-sm text-gray-600">
                      {item.notes}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-50 flex flex-col space-y-2 max-w-md">
        {state.toasts.map((toast) => (
          <Toast
            key={toast.id}
            type={toast.type}
            message={toast.message}
            onClose={() => removeToast(toast.id)}
            isVisible={true}
          />
        ))}
      </div>

      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-6xl mx-auto px-4">

          {/* Back */}
          <button
            onClick={() =>
              navigate("/candidate/jobs", {
                state: { activeTab: "applications" },
              })
            }
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6"
          >
            ← Back to Applications
          </button>

          {/* HEADER CARD */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:justify-between gap-6">

              {/* Left */}
              <div className="flex gap-4 flex-1 min-w-0">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-sm">
                  <span className="text-white font-semibold text-lg">
                    {jobInitials}
                  </span>
                </div>

                <div className="min-w-0">
                  <h1 className="text-2xl font-semibold text-gray-900 truncate">
                    {app.job.title}
                  </h1>

                  <p className="text-gray-600 font-medium">
                    {app.job.company_name || "Company"}
                  </p>

                  <div className="mt-2 text-sm text-gray-500">
                    {app.job.location || "Location not specified"}
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    {app.job.job_type && (
                      <span className="px-3 py-1 text-xs rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 font-medium">
                        {app.job.job_type.replace("_", " ")}
                      </span>
                    )}

                    <span
                      className={`px-3 py-1 text-xs rounded-full font-medium ${getStatusColor(
                        app.status
                      )}`}
                    >
                      {app.status.replace(/_/g, " ").toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right */}
              <div className="text-sm text-gray-600 lg:text-right">
                <div className="mb-1 font-medium text-gray-900">
                  {app.job.salary_currency} {app.job.salary_range || "Not Disclosed"}
                </div>
                <div>
                  Applied:
                  <span className="ml-1 font-medium text-gray-900">
                    {formatDateTime(app.applied_at)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* TABS */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm mb-6">
            <div className="flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex-1 py-4 text-sm font-medium transition-all ${activeTab === tab.id
                      ? "text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50"
                      : "text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* TAB CONTENT */}
          {activeTab === "overview" ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* LEFT SIDE */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                  <h2 className="text-lg font-semibold mb-4">
                    Job Description
                  </h2>

                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-h-80 overflow-y-auto">
                    <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                      {app.job.description || "No description provided."}
                    </p>
                  </div>
                </div>
              </div>

              {/* RIGHT SIDE */}
              {/* <div>
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                  <h2 className="text-lg font-semibold mb-6">
                    Application Documents
                  </h2>

                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      Resume
                    </h3>

                    {app.resume ? (
                      <div className="flex justify-between items-center bg-gray-50 border border-gray-200 rounded-xl p-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {app.resume.filename}
                          </p>
                          <p className="text-xs text-gray-500">
                            Uploaded with application
                          </p>
                        </div>

                        <button
                          onClick={handleDownloadClick}
                          disabled={state.downloadLoading}
                          className="px-4 py-2 text-sm border border-indigo-200 rounded-lg text-indigo-600 hover:bg-indigo-50 transition"
                        >
                          {state.downloadLoading
                            ? "Downloading..."
                            : "Download"}
                        </button>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-500">
                        No resume attached
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      Cover Letter
                    </h3>

                    {app.cover_letter ? (
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-700">
                        Submitted with application
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-500">
                        No cover letter provided
                      </div>
                    )}
                  </div>
                </div>
              </div> */}
            </div>
          ) : (
            <Timeline />
          )}

          {/* ACTION */}
          {activeTab === "overview" && (
            <div className="mt-10 flex justify-end">
              {canWithdrawApplication(app.status) ? (
                <button
                  onClick={handleWithdrawClick}
                  disabled={state.withdrawLoading}
                  className={`px-6 py-3 rounded-xl font-medium transition ${state.withdrawLoading
                      ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                      : "bg-red-600 text-white hover:bg-red-700"
                    }`}
                >
                  {state.withdrawLoading
                    ? "Withdrawing..."
                    : "Withdraw Application"}
                </button>
              ) : ['withdrawn', 'rejected'].includes(app.status.toLowerCase()) ? (
                <button
                  className="bg-indigo-600 text-white px-6 py-3 rounded-xl hover:bg-indigo-700 transition font-medium"
                  onClick={() =>
                    navigate(`/candidate/jobs?query=${encodeURIComponent(app.job.title)}`)
                  }
                >
                  View Similar Jobs
                </button>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <WithdrawConfirmationModal
        isOpen={showWithdrawModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmWithdraw}
        jobTitle={app.job.title}
        loading={state.withdrawLoading}
      />
    </>
  );
};

export default JobApplicationDetailsPage;
